import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from "bootstrap-4-react/lib/components";
import Table from "react-bootstrap/Table";
import LoaderSpinner from '../common/Loader';
import BatchPagination from '../batch/BatchPagination';
import { toast } from "react-toastify";
import moment from 'moment';
import { numOfPage } from '../../constant';
import {
    getWalmartFeedList,
    getWalmartItemBySku,
} from '../../services/walmart.service';

const WalmartFeeds = () => {
    const navigate = useNavigate();
    const [feeds, setFeeds] = useState([]);
    const [totalResults, setTotalResults] = useState(0);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [numberOfPage, setNumberOfPage] = useState(30);
    const [syncSku, setSyncSku] = useState('');

    const userDetails = useSelector((state) => state.auth.user);
    const companyList = useSelector((state) => state.auth.companyList);

    const companySellerId = useMemo(() => {
        const currentCompany = JSON.parse(localStorage.getItem('currentCompany') || '{}');
        if (currentCompany?.companySellerId) return currentCompany.companySellerId;
        if (userDetails?.Value?.companySellerId && userDetails.Value.companySellerId !== 0) {
            return userDetails.Value.companySellerId;
        }
        if (companyList?.length > 0) return companyList[0].companySellerId;
        return null;
    }, [userDetails, companyList]);

    const fetchFeeds = useCallback(async () => {
        if (!companySellerId) {
            toast.error('No seller context found. Please select a company/seller.');
            setLoading(false);
            return;
        }
        setLoading(true);
        const result = await getWalmartFeedList(companySellerId, numberOfPage, (currentPage - 1) * numberOfPage);
        if (result?.error === 'WALMART_NOT_CONNECTED') {
            toast.error(result.message || 'Walmart account is not connected.');
        } else if (result) {
            setFeeds(result.results?.feed || []);
            setTotalResults(result.totalResults || 0);
        } else {
            toast.error('Failed to fetch feeds from Walmart');
        }
        setLoading(false);
    }, [companySellerId, currentPage, numberOfPage]);

    useEffect(() => {
        fetchFeeds();
    }, [fetchFeeds]);

    const handleSyncItem = async () => {
        if (!syncSku.trim()) {
            toast.error('Please enter a SKU');
            return;
        }
        if (!companySellerId) {
            toast.error('No seller context found. Please select a company/seller.');
            return;
        }
        setLoading(true);
        const result = await getWalmartItemBySku(companySellerId, syncSku.trim());
        if (result?.error === 'WALMART_NOT_CONNECTED') {
            toast.error(result.message || 'Walmart account is not connected.');
        } else if (result) {
            toast.success('Item synced successfully');
            setSyncSku('');
        } else {
            toast.error('Failed to fetch item from Walmart');
        }
        setLoading(false);
    };

    const handleViewDetails = (feed) => {
        navigate(`/walmart-feeds/detail/${feed.feedId}`);
    };

    // --- Rendering helpers ---

    const getStatusBadge = (status) => {
        if (!status) return <span className="activeStatus">UNKNOWN</span>;
        const isError = ['ERROR', 'DATA_ERROR'].includes(status);
        if (isError) return <span className="errorStatus">{status}</span>;
        return <span className="activeStatus">{status}</span>;
    };

    const formatEpoch = (epoch) => {
        if (!epoch) return '-';
        return moment(epoch).format("MM-DD-YYYY h:mm A");
    };

    const totalPages = Math.ceil(totalResults / numberOfPage);

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec mb-1">
                    <div>Walmart Feeds</div>
                    <div className="top_right_button_container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter SKU"
                            value={syncSku}
                            onChange={(e) => setSyncSku(e.target.value)}
                            style={{ width: '180px' }}
                            disabled={loading}
                        />
                        <Button
                            className="cus-seconday-bg-btn top-btn"
                            onClick={handleSyncItem}
                            disabled={loading}
                        >
                            Sync Item
                        </Button>
                        <Button
                            className="cus-seconday-bg-btn top-btn"
                            onClick={fetchFeeds}
                        >
                            <i className="fa fa-refresh" aria-hidden="true"></i> Refresh
                        </Button>
                    </div>
                </div>

                {loading && (
                    <div className="full-screen-spinner-overlay"><LoaderSpinner /></div>
                )}

                <div className="main_content cus-page-content-sec">
                    <div className="main_content_table">
                        <div className="tableFixHead">
                            <Table className={`tableWide table-height cus-left-right-col-layout ${loading ? "disableTble" : ""}`}>
                                <thead>
                                    <tr>
                                        <th width="4%">#</th>
                                        <th width="20%">Feed ID</th>
                                        <th width="12%">Feed Type</th>
                                        <th width="12%">Status</th>
                                        <th width="8%">Received</th>
                                        <th width="8%">Succeeded</th>
                                        <th width="8%">Failed</th>
                                        <th width="15%">Date</th>
                                        <th width="13%">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!loading && feeds.length > 0 && feeds.map((feed, index) => (
                                        <tr key={feed.feedId}>
                                            <td>{(currentPage - 1) * numberOfPage + index + 1}</td>
                                            <td style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                                                {feed.feedId}
                                            </td>
                                            <td>{feed.feedType}</td>
                                            <td>{getStatusBadge(feed.feedStatus)}</td>
                                            <td>{feed.itemsReceived || 0}</td>
                                            <td>{feed.itemsSucceeded || 0}</td>
                                            <td>{feed.itemsFailed || 0}</td>
                                            <td>{formatEpoch(feed.feedDate)}</td>
                                            <td>
                                                <button
                                                    className="btn cus-seconday-bg-btn"
                                                    onClick={() => handleViewDetails(feed)}
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {!loading && feeds.length === 0 && (
                                        <tr>
                                            <td colSpan="9" className="text-center">No feed submissions found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </div>

                    {totalResults > 0 && (
                        <BatchPagination
                            gridData={feeds}
                            numberOfPage={numberOfPage}
                            numOfPage={numOfPage}
                            loader={loading}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalRecords={totalResults}
                            setCurrentPage={(value) => setCurrentPage(value)}
                            setNumberOfPage={(value) => { setNumberOfPage(Number.parseInt(value)); setCurrentPage(1); }}
                        />
                    )}
                </div>
            </div>
        </main>
    );
};

export default WalmartFeeds;
